import m from '@bitstillery/mithril'
import {Button, ButtonGroup, Dialog} from '@bitstillery/common/components'
import {$t} from '@bitstillery/common/app'
import {present} from '@bitstillery/common/lib/utils'

interface ShowDialogAttrs {
    body: () => m.Children
    className?: string
    confirm?: {
        action: () => unknown
        icon: string
        text: string
    }
    /**
     * Buttons rendered beside cancel and confirm, for a decision with more than one way forward.
     * A dialog that asks "delete, or keep it and change what it means?" has two real answers, and
     * burying the second one in the body reads as an afterthought.
     */
    extra_actions?: m.Children
    model: {
        loading?: boolean
        type?: 'brand' | 'danger' | 'surface' | 'default' | 'info' | 'success' | 'warning' | 'unset' | (string & {})
        title?: string
    }
    onclose?: () => unknown
    onopen?: () => unknown
    /** `wide` for a body that is content to work through rather than a question to answer. */
    size?: 'default' | 'wide'
}

/** Remove insert location and recreate the insert location. **/
function remove_element_from_dom() {
    const element = document.querySelector('#dialog-confirmation')
    const parent = element?.parentElement
    const new_element = document.createElement('div')
    new_element.id = 'dialog-confirmation'
    if (element) {
        // Removing the element alone keeps the mount registered, so every later redraw re-runs the dialog body.
        m.mount(element, null)
    }
    element?.remove()
    parent?.append(new_element)
}

export function mount_dialog(attrs: ShowDialogAttrs) {
    const element = present(document.querySelector('#dialog-confirmation'), 'dialog insertion point')

    m.mount(element, {
        view: () => (
            <Dialog
                {...attrs}
                body={attrs.body}
                footer={
                    <ButtonGroup>
                        {attrs.confirm && (
                            <Button
                                disabled={attrs.model.loading}
                                onclick={() => {
                                    remove_element_from_dom()
                                }}
                                text={$t('dialog.cancel')}
                            />
                        )}
                        {attrs.extra_actions}
                        {attrs.confirm && (
                            <Button
                                disabled={attrs.model.loading}
                                loading={attrs.model.loading}
                                icon={attrs.confirm.icon}
                                onclick={() => {
                                    if (!attrs.confirm) {
                                        return
                                    }
                                    attrs.confirm.action()
                                }}
                                text={attrs.confirm.text}
                                type={attrs.model.type ? attrs.model.type : 'default'}
                            ></Button>
                        )}
                    </ButtonGroup>
                }
                loading={attrs.model.loading}
                onclose={() => {
                    remove_element_from_dom()
                    attrs.onclose?.()
                }}
                size={attrs.size}
                title={attrs.model.title}
                type={attrs.model.type ? attrs.model.type : 'default'}
            >
                {attrs.body()}
            </Dialog>
        ),
    })

    return {
        close: () => {
            remove_element_from_dom()
        },
    }
}
